'use client';

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchSeriesList, fetchCourseList } from '@/lib/academy/academy-api';
import { trackPageDuration } from '@/src/utils/analytics';
import type { CourseSeries, CollegeCourseItem } from '@zrt-ai-office/types/course';
import { CategoryHero, CourseGrid } from '@/src/components/academy';
import { getGameCourseList } from '@/lib/academy/game-mock-data';
import { Gamepad2 } from 'lucide-react';

/** 临时、可撤销：隐藏学院页的“功能测试”课程入口；恢复时改回 true。 */
const SHOW_FUNCTION_TEST_COURSES = false;

export function CoursesPage() {
	const navigate = useNavigate();
	const [series, setSeries] = useState<CourseSeries[]>([]);
	const [courses, setCourses] = useState<CollegeCourseItem[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => trackPageDuration('academy'), []);

	const loadData = () => {
		setLoading(true);
		Promise.all([fetchSeriesList(), fetchCourseList()])
			.then(([seriesResp, coursesResp]) => {
				setSeries(seriesResp.list);
				setCourses(coursesResp.list);
			})
			.finally(() => setLoading(false));
	};

	useEffect(() => {
		loadData();
		const onCourseChanged = () => loadData();
		window.addEventListener('academy:course-changed', onCourseChanged);
		return () => window.removeEventListener('academy:course-changed', onCourseChanged);
	}, []);

	const courseSectionRef = useRef<HTMLElement>(null);

	const handleBrowseCourses = () => {
		courseSectionRef.current?.scrollIntoView({ behavior: 'smooth' });
	};

	const handleCourseClick = (courseId: number) => {
		navigate(`/academy/${courseId}`);
	};

	const handleGameCourseClick = (courseId: number) => {
		navigate(`/academy/game/${courseId}`);
	};

	// Compute grouped vs ungrouped courses
	const seriesIds = new Set(series.map((s) => s.id));
	const groupedBySeriesHasContent = series.some((s) => courses.some((c) => c.seriesId === s.id));
	const ungroupedCourses = courses.filter((c) => !seriesIds.has(c.seriesId));

	// Game courses from mock data
	const gameCourses = getGameCourseList();

	return (
		<div className="min-h-dvh bg-slate-50">
			{/* Hero */}
			<CategoryHero onBrowseCourses={handleBrowseCourses} />

			{/* Series sections or flat course grid */}
			<section className="mx-auto max-w-7xl px-6 py-12" ref={courseSectionRef}>
				{loading ? (
					// Loading skeleton — matches final layout structure
					<div>
						<div className="h-7 w-32 bg-slate-200 rounded animate-pulse mb-2" />
						<div className="h-4 w-48 bg-slate-200/60 rounded animate-pulse mb-6" />
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
							{[1, 2, 3].map((i) => (
								<div className="rounded-2xl bg-white border border-slate-200/60 overflow-hidden animate-pulse" key={i}>
									<div className="h-44 bg-slate-200" />
									<div className="p-5 space-y-3">
										<div className="flex gap-4">
											<div className="h-3 w-16 bg-slate-200 rounded" />
											<div className="h-3 w-20 bg-slate-200 rounded" />
										</div>
										<div className="h-5 w-3/4 bg-slate-200 rounded" />
										<div className="h-3 w-full bg-slate-100 rounded" />
										<div className="h-8 w-24 bg-slate-200 rounded-lg mt-2" />
									</div>
								</div>
							))}
						</div>
					</div>
				) : (
					<div className="space-y-16">
						{/* Regular courses */}
						{groupedBySeriesHasContent ? (
							<>
								{series.map((s) => {
									const seriesCourses = courses.filter((c) => c.seriesId === s.id);
									if (seriesCourses.length === 0) return null;
									return (
										<div key={s.id}>
											<h2 className="text-2xl font-bold text-slate-900 mb-2">{s.title}</h2>
											<p className="text-sm text-slate-500 mb-6">{s.description}</p>
											<CourseGrid courses={seriesCourses} onCourseClick={handleCourseClick} />
										</div>
									);
								})}
								{/* Show courses that don't belong to any series */}
								{ungroupedCourses.length > 0 && (
									<div>
										<h2 className="text-2xl font-bold text-slate-900 mb-2">全部课程</h2>
										<p className="text-sm text-slate-500 mb-6">更多精选课程</p>
										<CourseGrid courses={ungroupedCourses} onCourseClick={handleCourseClick} />
									</div>
								)}
							</>
						) : (
							// Flat course grid (no series match, or no series)
							courses.length > 0 && (
								<div>
									<h2 className="text-2xl font-bold text-slate-900 mb-2">全部课程</h2>
									<p className="text-sm text-slate-500 mb-6">探索我们的精选课程</p>
									<CourseGrid courses={courses} onCourseClick={handleCourseClick} />
								</div>
							)
						)}

						{/* 功能测试栏 */}
						{SHOW_FUNCTION_TEST_COURSES && gameCourses.length > 0 && (
							<div className="border-t border-slate-200 pt-8">
								<p className="text-xs text-slate-400 mb-3">功能测试</p>
								<div className="flex flex-wrap gap-2">
									{gameCourses.map((gc) => (
										<button
											className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-500 transition-colors hover:border-emerald-300 hover:text-emerald-600 hover:bg-emerald-50"
											key={`test-${gc.id}`}
											onClick={() => handleGameCourseClick(gc.id)}
										>
											<Gamepad2 size={12} />
											{gc.title}
										</button>
									))}
								</div>
							</div>
						)}
					</div>
				)}
			</section>
		</div>
	);
}
